import { user as userTools } from '@sellpy/commons'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect, useState } from 'react'
import config from 'config'
import { useMutation, useQuery } from '@apollo/client'
import {
  analyticsDisableAnalytics,
  analyticsDisableEmailMarketing,
  analyticsDisableExternalMarketing,
  analyticsEnableAnalytics,
  analyticsEnableEmailMarketing,
  analyticsEnableExternalMarketing,
  analyticsSetUserEmail,
  analyticsSetUserId,
  analyticsSetUserPhone,
  analyticsUnsetUserEmail,
  analyticsUnsetUserId,
  analyticsUnsetUserPhone
} from '../../../common/analytics/actions'
import { getCurrentUser, useCurrentUser } from '../../../common/user/currentApolloUser'
import {
  PERMISSION_TYPE_ANALYTICS,
  PERMISSION_TYPE_EXTERNAL_MARKETING,
  PERMISSION_TYPE_EMAIL_MARKETING
} from '../../../common/user/dataHandling'
import { getApolloClient } from '../../apollo/apolloClientSingleton'
import { getRegion } from '../../../common/region/region'
import { setDataUsageConsentForUser } from './dataConsent.mutation.gql'
import { getDeviceDataPermissionsForInstallationId } from './devicePermissions.query.gql'
import { getUserDataPermissions } from './userPermissions.query.gql'
import { getEmailMarketingPermission } from './emailPermissions.query.gql'

const CONSENTS_LOCALE_STORAGE_KEY = (consentType) =>
  `deviceDataConsent_${consentType}_${config.parse.applicationId}`

const CLIENT_CONSENTS = [PERMISSION_TYPE_ANALYTICS, PERMISSION_TYPE_EXTERNAL_MARKETING]

export const initializeDeviceDataConsent = async () => {
  const deviceDataConsent = await getDeviceDataConsent()
  if (deviceDataConsent[PERMISSION_TYPE_ANALYTICS]) analyticsEnableAnalytics()
  else analyticsDisableAnalytics()

  if (deviceDataConsent[PERMISSION_TYPE_EXTERNAL_MARKETING]) analyticsEnableExternalMarketing()
  else analyticsDisableExternalMarketing()
}

export const initializeEmailMarketingConsent = async () => {
  const apolloClient = getApolloClient()
  const currentUser = await getCurrentUser()
  if (userTools.userIsFull(currentUser)) {
    const { data: { emailMarketingPermission } = {} } = await apolloClient.query({
      query: getEmailMarketingPermission,
      fetchPolicy: 'network-only'
    })
    if (emailMarketingPermission?.consent) {
      analyticsEnableEmailMarketing()
    } else {
      analyticsDisableEmailMarketing()
    }
  }
}

export const useAnalyticsConsent = () => {
  const [analyticsConsent, setAnalyticsConsent] = useState()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchConsent = async () => {
      const consent = await getDeviceDataConsent()
      setLoading(false)
      setAnalyticsConsent(consent[PERMISSION_TYPE_ANALYTICS])
    }
    fetchConsent()
  }, [])

  const updateAnalyticsConsent = async (value) => {
    setAnalyticsConsent(value)
    await _updateDeviceDataConsent({ consentType: PERMISSION_TYPE_ANALYTICS, value })
  }

  return { analyticsConsent, updateAnalyticsConsent, loading }
}

export const useExternalMarketingConsent = () => {
  const [externalMarketingConsent, setExternalMarketingConsent] = useState()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchConsent = async () => {
      const consent = await getDeviceDataConsent()
      setExternalMarketingConsent(consent[PERMISSION_TYPE_EXTERNAL_MARKETING])
      setLoading(false)
    }
    fetchConsent()
  }, [])

  const updateExternalMarketingConsent = async (value) => {
    setExternalMarketingConsent(value)
    await _updateDeviceDataConsent({ consentType: PERMISSION_TYPE_EXTERNAL_MARKETING, value })
  }

  return { externalMarketingConsent, updateExternalMarketingConsent, loading }
}

const _getInstallationIdDataConsents = async () => {
  const apolloClient = getApolloClient()
  const { data: { installationIdDataPermissions = [] } = {} } = await apolloClient.query({
    query: getDeviceDataPermissionsForInstallationId
  })
  return CLIENT_CONSENTS.reduce((acc, consentType) => {
    const installationIdConsent = installationIdDataPermissions.find(
      (consent) => consent.type === consentType
    )
    if (installationIdConsent) {
      acc[consentType] = installationIdConsent.consent
    }
    return acc
  }, {})
}

const getDeviceDataConsent = async () => {
  const rawAnalyticsValue = window.localStorage.getItem(
    CONSENTS_LOCALE_STORAGE_KEY(PERMISSION_TYPE_ANALYTICS)
  )
  const rawExternalMarketingValue = window.localStorage.getItem(
    CONSENTS_LOCALE_STORAGE_KEY(PERMISSION_TYPE_EXTERNAL_MARKETING)
  )
  const data = {}
  if (rawAnalyticsValue != null) data[PERMISSION_TYPE_ANALYTICS] = JSON.parse(rawAnalyticsValue)
  if (rawExternalMarketingValue != null)
    data[PERMISSION_TYPE_EXTERNAL_MARKETING] = JSON.parse(rawExternalMarketingValue)

  if (!isEmpty(data)) return data

  const installationIdDataConsents = await _getInstallationIdDataConsents()

  Object.entries(installationIdDataConsents).forEach(([consentType, value]) => {
    window.localStorage.setItem(CONSENTS_LOCALE_STORAGE_KEY(consentType), JSON.stringify(value))
  })
  return installationIdDataConsents
}

const _updateDeviceDataConsent = async ({ consentType, value }) => {
  const apolloClient = getApolloClient()
  await apolloClient.mutate({
    mutation: setDataUsageConsentForUser,
    variables: {
      type: consentType,
      consent: value,
      region: getRegion()
    }
  })

  window.localStorage.setItem(CONSENTS_LOCALE_STORAGE_KEY(consentType), JSON.stringify(value))

  if (consentType === PERMISSION_TYPE_ANALYTICS) await refreshAnalyticsTracking(value)
  if (consentType === PERMISSION_TYPE_EXTERNAL_MARKETING)
    await refreshExternalMarketingTracking(value)
}

const refreshAnalyticsTracking = async (consentValue) => {
  if (consentValue) analyticsEnableAnalytics()
  else analyticsDisableAnalytics()

  await refreshUserIdTracking()
}

const refreshExternalMarketingTracking = async (consentValue) => {
  if (consentValue) analyticsEnableExternalMarketing()
  else analyticsDisableExternalMarketing()
}

export const useUserDataConsent = () => {
  const user = useCurrentUser()
  const { data, loading } = useQuery(getUserDataPermissions, {
    skip: !userTools.userIsFull(user)
  })
  const userDataConsents = (data?.userDataPermissions || []).reduce((consents, permission) => {
    consents[permission.type] = permission.consent
    return consents
  }, {})

  return { userDataConsents, loading }
}

export const useEmailMarketingConsent = (encryptedEmail) => {
  const user = useCurrentUser()
  const { data, loading } = useQuery(getEmailMarketingPermission, {
    variables: { encryptedEmail },
    skip: !(encryptedEmail || user?.email)
  })
  const emailMarketingConsent = data?.emailMarketingPermission?.consent

  const [setEmailMarketingConsentMutation] = useMutation(setDataUsageConsentForUser)
  const setEmailMarketingConsent = useCallback(
    async ({ consent, encryptedEmail }) => {
      await setEmailMarketingConsentMutation({
        variables: {
          type: PERMISSION_TYPE_EMAIL_MARKETING,
          consent,
          encryptedEmail,
          region: getRegion()
        },
        refetchQueries: [
          {
            query: getEmailMarketingPermission,
            variables: { encryptedEmail }
          }
        ]
      })
      consent ? analyticsEnableEmailMarketing() : analyticsDisableEmailMarketing()
    },
    [setEmailMarketingConsentMutation]
  )

  return { emailMarketingConsent, loading, setEmailMarketingConsent }
}
export const refreshUserIdTracking = async () => {
  const deviceDataConsent = await getDeviceDataConsent()
  const user = await getCurrentUser()
  if (deviceDataConsent[PERMISSION_TYPE_ANALYTICS] && userTools.userIsFull(user)) {
    analyticsSetUserId(user.objectId)
    analyticsSetUserEmail({ email: user.email })
    analyticsSetUserPhone({ phone: user.address?.phone })
  } else {
    analyticsUnsetUserId()
    analyticsUnsetUserEmail()
    analyticsUnsetUserPhone()
  }
}
